/**
 * How much of a scroll container's bottom edge is currently covered by fixed
 * bottom UI — today that is the action panel.
 *
 * Scroll callers used to assume a constant (ACTION_PANEL_HEIGHT, plus the
 * safe area when the keyboard was closed). That assumption held on Android and
 * not on iOS, where the panel sits in a different place relative to the list,
 * so cards were scrolled to a position that put their own action row behind
 * the panel. Measuring instead makes every view and platform self-correcting:
 * when nothing covers the container, the inset is 0 and nothing is reserved.
 *
 * @see dreambase/ui/scroll-into-view.md
 */

import { ACTION_PANEL_HEIGHT } from "@/features/action-panel/constants"

/** Marks the fixed bottom toolbar so scroll code can measure it. */
export const ACTION_PANEL_ATTR = "data-action-panel"

/**
 * @param container The scrolling element the target lives in.
 * @param extra     Breathing room to add on top of what is covered.
 */
export function getBottomInset(container: HTMLElement, extra = 0): number {
  const panel = document.querySelector<HTMLElement>(`[${ACTION_PANEL_ATTR}]`)
  if (!panel) return extra

  const style = getComputedStyle(panel)
  const hidden =
    style.display === "none" ||
    style.visibility === "hidden" ||
    Number(style.opacity) === 0
  if (hidden) return extra

  const panelRect = panel.getBoundingClientRect()
  if (panelRect.height < 1) return extra

  // The panel animates its height (0 → ACTION_PANEL_HEIGHT) when a task opens.
  // Its bottom edge stays put, so derive the settled top from the bottom edge
  // instead of reading a height that is still growing.
  const paddingBottom = Number.parseFloat(style.paddingBottom) || 0
  const settledHeight = Math.max(panelRect.height, ACTION_PANEL_HEIGHT + paddingBottom)
  const settledTop = panelRect.bottom - settledHeight

  const covered = Math.max(0, container.getBoundingClientRect().bottom - settledTop)
  return covered + extra
}
